/* eslint-disable camelcase */
import { services } from '@qompium/pages-helper-tools';
import { Currency, getUpgradePriceAndId, GroupTypesB2C } from '../helpers/product';
import { essentialGroupId, premiumGroupId } from '../config';
import { redirectWithQueryParams, PagePath, calculateDifferenceInMonths } from '../helpers/util';
import * as apiClient from './apiClient';
import { ActivePrescription } from '../models';
import { Product } from './paymentsService';

const prescriptionServiceUrl = services.prescriptions;

enum Status {
  PAID_BY_GROUP = 'PAID_BY_GROUP',
  PAID_BY_USER = 'PAID_BY_USER',
  ACTIVATED = 'ACTIVATED',
  NOT_PAID = 'NOT_PAID',
  FREE = 'FREE',
}

export async function getFreeTrialPrescription(hash: string | null) {
  if (hash === null || hash === 'null' || hash === undefined) {
    return undefined;
  }
  const prescription = await apiClient.get<Prescription>(prescriptionServiceUrl, hash);

  if (!prescription?.status || prescription.status === Status.ACTIVATED) {
    return undefined;
  }
  return prescription;
}

export async function getActivePeriods(userId: string) {
  const endpoint = `periods/?user_id=${userId}&end_timestamp>${Date.now()}`;

  const response = await apiClient.get<PeriodResponse>(prescriptionServiceUrl, endpoint);

  return response?.data;
}

async function linkPrescription(hash: string) {
  const endpoint = `${hash}/scan`;

  return await apiClient.get<Prescription>(prescriptionServiceUrl, endpoint);
}

async function activatePrescription(hash: string) {
  const endpoint = `${hash}/activate`;

  const result = await apiClient.get<Prescription>(prescriptionServiceUrl, endpoint);

  return result && result?.status === Status.ACTIVATED;
}

export async function startFreeTrialPrescription(userId: string, prescription: Prescription | undefined) {
  if (!prescription) {
    throw new Error('No prescription to handle!');
  }
  let prescriptionToActivate: Prescription | undefined = prescription;

  if (prescriptionToActivate.user_id == null) {
    prescriptionToActivate = await linkPrescription(prescriptionToActivate.hash);
    if (!prescriptionToActivate || prescriptionToActivate.user_id == null) {
      throw new Error('No prescription after scan!');
    }
  }

  if (prescriptionToActivate.user_id !== userId) {
    throw new Error('Wrong user linked to prescription');
  }

  switch (prescriptionToActivate.status) {
    case Status.ACTIVATED:
      throw new Error('Prescription already activated');
    case Status.NOT_PAID:
      throw new Error('Not a free prescription to activate');
    case Status.PAID_BY_USER:
    case Status.PAID_BY_GROUP:
    case Status.FREE:
    default:
      return await activatePrescription(prescriptionToActivate.hash);
  }
}

export async function checkPrescriptionsInterval(userId: string, query: any) {
  let repetitions = 0;
  const maximumRepetitions = 30;
  const intervalID = setInterval(async () => {
    const activePeriods = await getActivePeriods(userId);
    if (activePeriods && activePeriods.find(period => period.group_id === essentialGroupId || period.group_id === premiumGroupId)) {
      clearInterval(intervalID);
      redirectWithQueryParams(PagePath.mySubscriptions, query);
      return;
    }

    if (repetitions === maximumRepetitions) {
      throw new Error('Maximum amount of repetitions is reached after payment...');
    }
    repetitions += 1;
  }, 1000);
}

export async function checkPrescriptionsIntervalOnDate(userId: string, endDatePrescription: number, query: any) {
  let repetitions = 0;
  const maximumRepetitions = 30;
  const intervalID = setInterval(async () => {
    const activePeriods = await getActivePeriods(userId);
    if (activePeriods && activePeriods.find(period => period.end_timestamp > endDatePrescription)) {
      clearInterval(intervalID);
      redirectWithQueryParams(PagePath.plans, query);
      return;
    }

    if (repetitions === maximumRepetitions) {
      throw new Error('Maximum amount of repetitions is reached after payment...');
    }
    repetitions += 1;
  }, 1000);
}

export async function filterMostImportantGroup(userConfiguration: any, activePeriods: any, products: Product[], currency: Currency) {
  const activeGroupIds = activePeriods.map((activePeriod: { group_id: any; }) => activePeriod.group_id);

  const activeList = userConfiguration.patientConfigurations.filter(
    (config: any) => activeGroupIds.includes(config.groupId) && Object.values(GroupTypesB2C).includes(config.data.groupType)
  );

  for (const activePeriod of activeList) {
    activePeriod.end = activePeriods.find((period: { group_id: string; }) => period.group_id === activePeriod.groupId).end_timestamp;
  }
  const mostImportantGroup = activeList.reduce(
    (accumulator: any, currentValue: any) => (
      isMoreImportantGroup(currentValue, accumulator) ? currentValue : accumulator
    )
  );

  const activeGroup = activePeriods.find((period: { group_id: string; }) => period.group_id === mostImportantGroup.groupId);

  const threeMonthsInMilliSeconds = 7776000000;
  const durationLongerThanThreeMonths = activeGroup.prescriptions.find(
    (prescription: { duration: number; }) => prescription.duration > threeMonthsInMilliSeconds
  );

  const calculatedUpgrade = !!(durationLongerThanThreeMonths);
  const currentMonthNumber = Math.floor(calculateDifferenceInMonths(Number(activeGroup.start_timestamp)));
  const upgradePriceAndId = await getUpgradePriceAndId(products, currentMonthNumber, calculatedUpgrade, currency);

  const prescription: ActivePrescription = {
    id: activeGroup.group_id,
    type: GroupTypesB2C[mostImportantGroup.data.groupType.toUpperCase()],
    start: Number(activeGroup.start_timestamp),
    end: Number(activeGroup.end_timestamp),
    ...upgradePriceAndId,
  };

  const { subscription } = userConfiguration.data;

  return { prescription, subscription };
}

function isMoreImportantGroup(valueToCompare: any, comparator: any) {
  if (!valueToCompare.data.groupType) {
    return false;
  }

  if (!comparator.data.groupType) {
    return true;
  }

  if (valueToCompare.data.groupType === comparator.data.groupType) {
    if (!valueToCompare.end) {
      return false;
    }
    if (!comparator.end) {
      return true;
    }
    return valueToCompare.end > comparator.end;
  }

  switch (valueToCompare.data.groupType) {
    case GroupTypesB2C.PREMIUM:
      return true;
    case GroupTypesB2C.ESSENTIAL:
      if (comparator.data.groupType === GroupTypesB2C.PREMIUM) return false;
      return true;
    case GroupTypesB2C.FREE_TRIAL:
      if (comparator.data.groupType === GroupTypesB2C.PREMIUM || comparator.data.groupType === GroupTypesB2C.ESSENTIAL) return false;
      return true;
    default:
      return false;
  }
}

export interface Prescription {
  id: string;
  status: string;
  hash: string;
  user_id: string;
  duration: number;
  start_timestamp: number;
  end_timestamp: number;
}

interface PeriodResponse {
  data: [
    {
      id: string;
      user_id: string;
      group_id: string;
      start_timestamp: number;
      end_timestamp: number;
      prescriptions: Prescription[];
    }
  ];
}
